import { createLocalVue, shallowMount } from '@vue/test-utils';
import Vuex from 'vuex';
import CartItem from './CartItem';
import VueCookies from 'vue-cookies';

import { getObjProperty } from "../../../utilities/mrVueUtils";
import { freeCurrency } from "../../../filters";

let updateItemQtyMock = jest.fn();
let updateItemSubscriptionMock = jest.fn();
let moveToOtherCartMock = jest.fn();
let removeFromCartMock = jest.fn();

function clearMocks() {
  updateItemQtyMock.mockClear();
  updateItemSubscriptionMock.mockClear();
  moveToOtherCartMock.mockClear();
  removeFromCartMock.mockClear();
}

const defaultStore = {
  modules: {
    cart: {
      namespaced: true,
      state: {
        editAllItems: false
      },

      getters: {
        freeItemsMap: jest.fn()
      },

      actions: {
        updateItemQty: updateItemQtyMock,
        updateItemSubscription: updateItemSubscriptionMock,
        moveToOtherCart: moveToOtherCartMock,
        removeFromCart: removeFromCartMock
      }
    },

    refillCart: {
      namespaced: true,
      state: {
        refillCart: {}
      }
    },

    subscriptions: {
      namespaced: true,
      state: {
        defaultShipFrequencies: '2, 3, 4, 5, 6, 7, 8'
      }
    },

    customer: {
      namespaced: true,
      state: {
        cdata: {}
      },

      getters: {
        hasMembershipPricing: jest.fn()
      }
    }

  }
};

const defaultItem = {
  index: 1,
  price: 19,
  name: 'Verona Brown',
  qty: 1,
  subscription: 0,
  url_key: 'verona-brown',
  isExtraTube: false,
  hasContent: true,
  desc: {
    short: 'Short Description'
  }
};

const createWrapper = (storeObj = defaultStore, item = defaultItem) => {
  let localVue = createLocalVue();
  localVue.use(Vuex);
  localVue.use(VueCookies);
  localVue.filter('freeCurrency', freeCurrency);

  let store = new Vuex.Store(storeObj);

  return shallowMount(CartItem, {
    store,
    localVue,
    propsData: { item },
    provide: { getObjProperty }
  });
};

describe('CartItem', () => {

  describe('computed', () => {
    describe('altText', () => {
      test('display item name after Madison Reed - ', () => {
        let localState1 = {
          item: {
            name: 'Bolzano Brown'
          }
        };

        let localState2 = {
          item: {
            name: 'Verona Brown'
          }
        };

        let res1 = CartItem.computed.altText.call(localState1);
        let res2 = CartItem.computed.altText.call(localState2);

        expect(res1).toEqual('Madison Reed - Bolzano Brown');
        expect(res2).toEqual('Madison Reed - Verona Brown');
      });
    });

    describe('imgSrc', () => {
      test('returns value of item.imagery.thumb.url', () => {
        let url = "/special/url";
        let localState = {
          item: {
            imagery: {
              thumb: {
                url
              }
            }
          },
          getObjProperty
        };

        expect(CartItem.computed.imgSrc.call(localState)).toEqual(url);
      });

      test('returns undefined when item.imagery does not exist', () => {
        let localState = {
          item: {},
          getObjProperty
        };

        expect(CartItem.computed.imgSrc.call(localState)).toEqual(undefined);
      });
    });

    describe('editing', () => {
      test('return true when localEditing is true but editAllItems is false', () => {
        let localState = { localEditing: true, editAllItems: false };
        expect(CartItem.computed.editing.call(localState)).toBe(true);
      });

      test('return true when localEditing is false but editAllItems is true', () => {
        let localState = { localEditing: false, editAllItems: true };
        expect(CartItem.computed.editing.call(localState)).toBe(true);
      });

      test('return true when localEditing is true and editAllItems is true', () => {
        let localState = { localEditing: true, editAllItems: true };
        expect(CartItem.computed.editing.call(localState)).toBe(true);
      });

      test('return false when localEditing is false and editAllItems is false', () => {
        let localState = { localEditing: false, editAllItems: false };
        expect(CartItem.computed.editing.call(localState)).toBe(false);
      });
    });

    describe('productUrl', () => {
      test('create producUrl based on item.url_key', () => {
        let localState1 = {
          item: {
            url_key: 'bolzano-brown'
          }
        };

        let localState2 = {
          item: {
            url_key: 'verona-brown'
          }
        };

        let res1 = CartItem.computed.productUrl.call(localState1);
        let res2 = CartItem.computed.productUrl.call(localState2);

        expect(res1).toEqual('/product/bolzano-brown');
        expect(res2).toEqual('/product/verona-brown');
      });
    });

    describe('itemTotalPrice', () => {
      test('calculate price based on item.qty and price', () => {
        let localState1 = {
          item: {
            price: 15,
            qty: 1
          }
        };

        let localState2 = {
          item: {
            price: 20,
            qty: 2
          }
        };

        let res1 = CartItem.computed.itemTotalPrice.call(localState1);
        let res2 = CartItem.computed.itemTotalPrice.call(localState2);

        expect(res1).toEqual(15);
        expect(res2).toEqual(40);
      });
    });

    describe('isItemEditable', () => {
      test('canBeSubscribed is true and has index', () => {
        let localState = {
          item: {
            index: 0,
            name: 'Verona Brown',
            price: 19,
            qty: 1,
            isExtraTube: false
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(true);
      });

      test('canBeSubscribed is true and does not have index', () => {
        let localState = {
          item: {
            price: 19,
            name: 'Verona Brown',
            qty: 1,
            isExtraTube: false
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(false);
      });

      test('canBeSubscribed is false and has index', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            qty: 1,
            isExtraTube: false
          },
          canBeSubscribed: false
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(false);
      });

      test('canBeSubscribed is true and is not extraTube', () => {
        let localState = {
          item: {
            index: 1,
            price: 19,
            name: 'Verona Brown',
            qty: 1,
            isExtraTube: false
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(true);
      });

      test('canBeSubscribed is true and is extraTube', () => {
        let localState = {
          item: {
            index: 1,
            price: 19,
            name: 'Verona Brown',
            qty: 1,
            isExtraTube: true
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(false);
      });

      test('canBeSubscribed is true and isBundle', () => {
        let localState = {
          item: {
            index: 1,
            price: 19,
            name: 'Verona Brown',
            qty: 1,
            isBundle: true
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(false);
      });

      test('canBeSubscribed is true and isBundle does not exist', () => {
        let localState = {
          item: {
            index: 1,
            price: 19,
            name: 'Verona Brown',
            qty: 1
          },
          canBeSubscribed: true
        };

        expect(CartItem.computed.isItemEditable.call(localState)).toBe(true);
      });
    });

    describe('canBeConvertedToAutoDelivery', () => {
      test('item is not a subscription, canBeSubscribed is true and refillCart does not exist', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            qty: 1
          },
          canBeSubscribed: true,
          refillCart: {}
        };

        expect(CartItem.computed.canBeConvertedToAutoDelivery.call(localState)).toBe(true);
      });

      test('item is a subscription, canBeSubscribed is true and refillCart does not exist', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 4,
            qty: 1
          },
          canBeSubscribed: true,
          refillCart: {}
        };

        expect(CartItem.computed.canBeConvertedToAutoDelivery.call(localState)).toBe(false);
      });

      test('item is not a subscription, canBeSubscribed is false and refillCart does not exist', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            qty: 1
          },
          canBeSubscribed: false,
          refillCart: {}
        };

        expect(CartItem.computed.canBeConvertedToAutoDelivery.call(localState)).toBe(false);
      });

      test('item is not a subscription, canBeSubscribed is true and refillCart does exist', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            qty: 1
          },
          canBeSubscribed: true,
          refillCart: {
            nextShipment: {}
          }
        };

        expect(CartItem.computed.canBeConvertedToAutoDelivery.call(localState)).toBe(false);
      });
    });

    describe('canAddToNextDelivery', () => {
      test('can add if item is not a subscription, inventory_status is not "discontinued", ', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            qty: 1
          },
          refillCart: {
            nextShipment: {}
          }
        };

        expect(CartItem.computed.canAddToNextDelivery.call(localState)).toBe(true);
      });

      test('can NOT add if item is a subscription', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 5,
            inventory_status: 'active',
            qty: 1
          },
          refillCart: {
            nextShipment: {}
          }
        };

        expect(CartItem.computed.canAddToNextDelivery.call(localState)).toBe(false);
      });

      test('can NOT add if inventory_status is "discontinued"', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'discontinued',
            qty: 1
          },
          refillCart: {
            nextShipment: {}
          }
        };

        expect(CartItem.computed.canAddToNextDelivery.call(localState)).toBe(false);
      });

      test('can NOT add if refillCart does not exist', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            qty: 1
          },
          refillCart: {}
        };

        expect(CartItem.computed.canAddToNextDelivery.call(localState)).toBe(false);
      });

      test('can NOT add if item is a giftCard', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            giftCard: {},
            qty: 1
          },
          refillCart: {
            nextShipment: {}
          }
        };

        expect(CartItem.computed.canAddToNextDelivery.call(localState)).toBe(false);
      });
    });

    describe('canBeSubscribed', () => {
      test('return true when item has product_type_sub_frequencies and inventory_status is not "discontinued"', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            product_type_sub_frequencies: "2,3,4,5,6,7,8",
            qty: 1
          }
        };

        expect(CartItem.computed.canBeSubscribed.call(localState)).toBe(true);
      });

      test('return false when item.product_type_sub_frequencies is null and inventory_status is not "discontinued"', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            product_type_sub_frequencies: null,
            qty: 1
          }
        };

        expect(CartItem.computed.canBeSubscribed.call(localState)).toBe(false);
      });

      test('return false when item.product_type_sub_frequencies does not exist and inventory_status is not "discontinued"', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            qty: 1
          }
        };

        expect(CartItem.computed.canBeSubscribed.call(localState)).toBe(false);
      });

      test('return false when item has product_type_sub_frequencies and inventory_status is "discontinued"', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'discontinued',
            product_type_sub_frequencies: "2,3,4,5,6,7,8",
            qty: 1
          }
        };

        expect(CartItem.computed.canBeSubscribed.call(localState)).toBe(false);
      });
    });

    describe('canEditQty', () => {
      test('return true when item is not an extra tube or gift card or bundle', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            inventory_status: 'active',
            product_type_sub_frequencies: "2,3,4,5,6,7,8",
            qty: 1
          }
        };

        expect(CartItem.computed.canEditQty.call(localState)).toBe(true);
      });

      test('return false when item isExtraTube', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            isExtraTube: true,
            inventory_status: 'active',
            qty: 1
          }
        };

        expect(CartItem.computed.canEditQty.call(localState)).toBe(false);
      });

      test('return false when item is a giftCard', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            giftCard: {},
            inventory_status: 'active',
            qty: 1
          }
        };

        expect(CartItem.computed.canEditQty.call(localState)).toBe(false);
      });
    });

    describe('showSubFrequencyTag', () => {
      test('return true when item is a subscription, is not in edit state and showAttachableFrequency is false', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 4,
            giftCard: {},
            inventory_status: 'active',
            qty: 1
          },
          editing: false,
          showAttachableFrequency: false
        };

        expect(CartItem.computed.showSubFrequencyTag.call(localState)).toBe(true);
      });

      test('return false when item is not a subscription, is not in edit state and showAttachableFrequency is false', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 0,
            giftCard: {},
            inventory_status: 'active',
            qty: 1
          },
          editing: false,
          showAttachableFrequency: false
        };

        expect(CartItem.computed.showSubFrequencyTag.call(localState)).toBe(false);
      });

      test('return false when item is a subscription, is in edit state and showAttachableFrequency is false', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 6,
            giftCard: {},
            inventory_status: 'active',
            qty: 1
          },
          editing: true,
          isItemEditable: true,
          showAttachableFrequency: false
        };

        expect(CartItem.computed.showSubFrequencyTag.call(localState)).toBe(false);
      });

      test('return false when item is a subscription, is not edit state and showAttachableFrequency is true', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 6,
            giftCard: {},
            inventory_status: 'active',
            qty: 1
          },
          editing: true,
          showAttachableFrequency: true
        };

        expect(CartItem.computed.showSubFrequencyTag.call(localState)).toBe(false);
      });
    });

    describe('showSimpleEdit', () => {
      test('return true when isItemEditable and showSubFrequencyTag are true and item is not in edit mode', () => {
        let localState = {
          isItemEditable: true,
          editing: false,
          showSubFrequencyTag: true
        };

        expect(CartItem.computed.showSimpleEdit.call(localState)).toBe(true);
      });

      test('return false when isItemEditable is false and showSubFrequencyTag is true and item is not in edit mode', () => {
        let localState = {
          isItemEditable: false,
          editing: false,
          showSubFrequencyTag: true
        };

        expect(CartItem.computed.showSimpleEdit.call(localState)).toBe(false);
      });

      test('return false when isItemEditable is false and showSubFrequencyTag is true and item is not in edit mode', () => {
        let localState = {
          isItemEditable: false,
          editing: false,
          showSubFrequencyTag: true
        };

        expect(CartItem.computed.showSimpleEdit.call(localState)).toBe(false);
      });

      test('return false when isItemEditable is true and showSubFrequencyTag is true and item is in edit mode', () => {
        let localState = {
          isItemEditable: true,
          editing: true,
          showSubFrequencyTag: true
        };

        expect(CartItem.computed.showSimpleEdit.call(localState)).toBe(false);
      });

      test('return false when isItemEditable is true and showSubFrequencyTag is false and item is not in edit mode', () => {
        let localState = {
          isItemEditable: true,
          editing: false,
          showSubFrequencyTag: false
        };

        expect(CartItem.computed.showSimpleEdit.call(localState)).toBe(false);
      });
    });

    describe('showAttachableFrequency', () => {
      test('return true when item.attachableFrequency is truthy and item is not an extra tube', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 6,
            inventory_status: 'active',
            qty: 1,
            isExtraTube: false,
            attachableFrequency: 6
          }
        };

        expect(CartItem.computed.showAttachableFrequency.call(localState)).toBe(true);
      });

      test('return false when item.attachableFrequency is truthy and item is an extra tube', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 6,
            inventory_status: 'active',
            qty: 1,
            isExtraTube: true,
            attachableFrequency: 6
          }
        };

        expect(CartItem.computed.showAttachableFrequency.call(localState)).toBe(false);
      });

      test('return false when item.attachableFrequency is not present and item is not an extra tube', () => {
        let localState = {
          item: {
            index: 0,
            price: 19,
            name: 'Verona Brown',
            subscription: 6,
            inventory_status: 'active',
            qty: 1,
            isExtraTube: false
          }
        };

        expect(CartItem.computed.showAttachableFrequency.call(localState)).toBe(false);
      });
    });

    describe('subFrequencyOptions', () => {
      test('return array of objects when item has product_type_sub_frequencies', () => {
        let localState = {
          item: {
            product_type_sub_frequencies: "3,4,5,6,7,8,9,10",
            one_time_price: 26,
            sub_price: 22,
            oneTimePriceKey: 'one_time_price',
            subPriceKey: 'sub_price',
          },
          defaultFrequencies: '2, 4, 6, 8, 10, 12, 13, 14, 15, 16'
        };

        let res = CartItem.computed.subFrequencyOptions.call(localState);

        expect(Array.isArray(res)).toBe(true);
        expect(res.length).toEqual(9);
        expect(res[1]).toEqual({ val: 3, label: 'Every 3 weeks - $22.00' });
        expect(res.pop()).toEqual({ val: 10, label: 'Every 10 weeks - $22.00' });
      });

      test('add default 0 value to the array for One Time option', () => {
        let localState = {
          item: {
            product_type_sub_frequencies: "3,4,5,6,7,8,9,10",
            one_time_price: 26,
            sub_price: 22,
            oneTimePriceKey: 'one_time_price',
            subPriceKey: 'sub_price'
          },
        };

        let res = CartItem.computed.subFrequencyOptions.call(localState);

        expect(res.length).toEqual(9);
        expect(res[0]).toEqual({ val: 0, label: 'One time - $26.00' });
      });

      test('return array of objects based on default sub frequencies when item does not have product_type_sub_frequencies', () => {
        let localState = {
          item: {
            one_time_price: 26,
            sub_price: 22,
            oneTimePriceKey: 'one_time_price',
            subPriceKey: 'sub_price'
          },
          defaultFrequencies: '2, 4, 6, 8, 10, 12, 13, 14, 15, 16'
        };

        let res = CartItem.computed.subFrequencyOptions.call(localState);

        expect(Array.isArray(res)).toBe(true);
        expect(res.length).toEqual(11);
        expect(res[0]).toEqual({ val: 0, label: 'One time - $26.00' });
        expect(res[1]).toEqual({ val: 2, label: 'Every 2 weeks - $22.00' });
        expect(res.pop()).toEqual({ val: 16, label: 'Every 16 weeks - $22.00' });
      });

      test('return array of objects with active_sub price keys', () => {
        let localState = {
          item: {
            one_time_price: 26,
            sub_price: 22,
            price_active_sub: 21,
            sub_price_active_sub: 20,
            oneTimePriceKey: 'price_active_sub',
            subPriceKey: 'sub_price_active_sub',
          },
          defaultFrequencies: '2, 4, 6, 8, 10, 12, 13, 14, 15, 16'
        };

        let res = CartItem.computed.subFrequencyOptions.call(localState);

        expect(res[0]).toEqual({ val: 0, label: 'One time - $21.00' });
        expect(res[1]).toEqual({ val: 2, label: 'Every 2 weeks - $20.00' });
        expect(res.pop()).toEqual({ val: 16, label: 'Every 16 weeks - $20.00' });
      });
    });
  });

  describe('methods', () => {
    beforeEach(clearMocks);

    describe('editProduct', () => {
      test('set localEditing to true', () => {
        const wrapper = createWrapper();
        wrapper.vm.editProduct();

        expect(wrapper.vm.localEditing).toBe(true);
      });
    });

    describe('updateProdQty', () => {
      test('calls expected method with expected payload', () => {
        const wrapper = createWrapper();
        const newQty = 2;
        wrapper.vm.updateProdQty(newQty);

        expect(updateItemQtyMock).toHaveBeenCalledTimes(1);
        expect(updateItemQtyMock.mock.calls[0][0]).toEqual(expect.any(Object), { index: defaultItem.index, qty: newQty, ctaSource: 'cart-edit-qty' });
      });
    });

    describe('removeItemFromCart', () => {
      test('expected method calls and payload', () => {
        let removeXSellToConvertItemSpy = jest.spyOn(CartItem.methods, 'removeXSellToConvertItem');
        const wrapper = createWrapper();
        wrapper.vm.removeItemFromCart();

        expect(removeFromCartMock).toHaveBeenCalledTimes(1);
        expect(removeFromCartMock.mock.calls[0][0]).toEqual(expect.any(Object), { index: defaultItem.index });
        expect(removeXSellToConvertItemSpy).toHaveBeenCalledTimes(1);
      });
    });

    describe('moveToRefillCart', () => {
      test('expected method call and payload', () => {
        const wrapper = createWrapper();
        wrapper.vm.moveToRefillCart();

        expect(moveToOtherCartMock).toHaveBeenCalledTimes(1);
        expect(moveToOtherCartMock.mock.calls[0][0]).toEqual(expect.any(Object), { from: 'cart', to: 'refill', index: defaultItem.index });
      });
    });
  });

});
